import React, { useEffect, useMemo, useRef, useState } from "react";
import { getIdTokenResult } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import db, { auth } from "../../../Firebase";
import ConsoleHomePageHeader from "../../Home/ConsoleHomePageHeader";
import { DATE_PRESETS, ACTIVE_USER_SAMPLE_LABEL } from "../analytics/constants";
import {
  clampRange,
  formatDateKeyPT,
  getLastFullMonthRanges,
  getPresetRange,
  isFullMonthRange,
} from "../analytics/date";
import {
  buildCohortWindows,
  buildDailySeries,
  computeEmotionalMetrics,
  computeHabitMetrics,
  computeKpis,
  computeMonthlyReport,
  computePlantDistribution,
  computeRetention,
  mergeCareActions,
} from "../analytics/compute";
import {
  fetchCareActions,
  fetchUsersBefore,
  fetchUsersInRange,
  fetchUsersByIds,
} from "../analytics/queries";
import "../ConsoleAnalyticsV2.css";

const LoadingText = ({ text = "Loading..." }) => (
  <div className="loadingText">{text}</div>
);

const ErrorBanner = ({ message }) =>
  message ? <div className="errorBanner">{message}</div> : null;

const KPICard = ({ label, value, helper }) => (
  <div className="kpiCard">
    <div className="kpiLabel">{label}</div>
    <div className="kpiValue">{value}</div>
    {helper ? <div className="kpiHelper">{helper}</div> : null}
  </div>
);

const TinyBarChart = ({ data, primary = "#2563eb" }) => {
  if (!data || data.length === 0) return <div className="chartEmpty">No data</div>;
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="tinyChart">
      {data.map((d) => {
        const height = Math.max(3, Math.round((d.value / max) * 60));
        return (
          <div className="tinyBar" key={d.label} title={`${d.label}: ${d.value}`}>
            <div className="tinyBarFill" style={{ height, background: primary }} />
          </div>
        );
      })}
    </div>
  );
};

const StackedBars = ({ data, colors }) => {
  if (!data || data.length === 0) return <div className="chartEmpty">No data</div>;
  const max = Math.max(...data.map((d) => d.waterings + d.chats), 1);
  return (
    <div className="tinyChart">
      {data.map((d) => {
        const total = d.waterings + d.chats;
        const height = Math.max(6, Math.round((total / max) * 70));
        const chatPct = total === 0 ? 0 : (d.chats / total) * 100;
        const waterPct = 100 - chatPct;
        return (
          <div className="tinyBar stacked" key={d.date} title={`${d.date} (${total})`}>
            <div
              className="tinyBarFill"
              style={{
                height,
                background: `linear-gradient(to top, ${colors.waterings} ${waterPct}%, ${colors.chats} ${waterPct}%)`,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

const DistributionBar = ({ buckets, note }) => {
  const total = (buckets || []).reduce((sum, b) => sum + b.value, 0);
  if (!buckets || buckets.length === 0) return <div className="chartEmpty">No data</div>;
  return (
    <div>
      <div className="distribution">
        {buckets.map((b) => {
          const pct = total === 0 ? 0 : (b.value / total) * 100;
          return (
            <div className="distributionSegment" key={b.label} style={{ width: `${pct}%` }}>
              <span>{b.label}</span>
              <strong>{b.value}</strong>
            </div>
          );
        })}
      </div>
      {note ? <div className="chartNote">{note}</div> : null}
    </div>
  );
};

const RetentionTable = ({ rows }) => {
  if (!rows || rows.length === 0) return <div className="chartEmpty">No retention data</div>;
  return (
    <div className="retentionTable">
      <div className="retentionRow header">
        <span>Cohort</span>
        <span>Size</span>
        <span>D7</span>
        <span>D30</span>
        <span>D90</span>
      </div>
      {rows.map((r) => (
        <div className="retentionRow" key={r.cohortLabel}>
          <span>{r.cohortLabel}</span>
          <span>{r.size}</span>
          <span>{r.d7 ?? "-"}</span>
          <span>{r.d30 ?? "-"}</span>
          <span>{r.d90 ?? "-"}</span>
        </div>
      ))}
    </div>
  );
};

const ConsoleAnalyticsV2 = () => {
  const [preset, setPreset] = useState("last30");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [range, setRange] = useState(getPresetRange("last30"));
  const [loading, setLoading] = useState({ overview: false, retention: false, monthly: false });
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [habit, setHabit] = useState(null);
  const [emotional, setEmotional] = useState(null);
  const [plantDistribution, setPlantDistribution] = useState(null);
  const [dailySeries, setDailySeries] = useState([]);
  const [stackedSeries, setStackedSeries] = useState([]);
  const [retentionMode, setRetentionMode] = useState("weekly");
  const [cohortCount, setCohortCount] = useState(12);
  const [retention, setRetention] = useState({ rows: [], trend: [] });
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState("");
  const cacheRef = useRef({});

  const rangeKey = useMemo(
    () => `${range.start?.toISOString?.() || ""}_${range.end?.toISOString?.() || ""}`,
    [range]
  );

  useEffect(() => {
    verifyAdmin();
  }, []);

  const verifyAdmin = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setAuthError("You must be signed in to view analytics.");
        setAuthReady(true);
        return;
      }
      const token = await getIdTokenResult(user);
      if (token?.claims?.admin) {
        setAuthReady(true);
        return;
      }
      const snap = await getDoc(doc(db, "Users", user.uid));
      if (snap.exists() && snap.data()?.isAdmin) {
        setAuthReady(true);
        return;
      }
      setAuthError("Admin access required.");
    } catch (err) {
      console.error("Admin check failed", err);
      setAuthError("Unable to verify admin access.");
    } finally {
      setAuthReady(true);
    }
  };

  useEffect(() => {
    if (!authReady || authError) return;
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, authReady, authError]);

  useEffect(() => {
    if (!authReady || authError) return;
    loadRetention();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retentionMode, cohortCount, authReady, authError]);

  useEffect(() => {
    if (!authReady || authError) return;
    loadMonthlyReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, authError]);

  const loadOverview = async () => {
    const cached = cacheRef.current[rangeKey];
    if (cached) {
      setOverview(cached.kpis);
      setPlantDistribution(cached.plantDistribution);
      setDailySeries(cached.dailySeries);
      setStackedSeries(cached.stackedSeries);
      setHabit(cached.habit);
      setEmotional(cached.emotional);
      return;
    }
    setLoading((s) => ({ ...s, overview: true }));
    setError("");
    try {
      const { waterings, chats } = await fetchCareActions(range.start, range.end);
      if (waterings.error || chats.error) {
        setError("Missing Firestore read permissions for analytics collections.");
        setLoading((s) => ({ ...s, overview: false }));
        return;
      }
      const actions = mergeCareActions(waterings.data, chats.data);
      const monthRange = isFullMonthRange(range.start, range.end);

      const daily = buildDailySeries(actions, range.start, range.end);
      const stacked = daily.map((d) => ({ ...d, label: d.date }));

      const usersSnap = await fetchUsersBefore(range.end);
      if (usersSnap.error) {
        setError("Missing permission to read Users for plant distribution.");
        setLoading((s) => ({ ...s, overview: false }));
        return;
      }
      let plantUsers = usersSnap.data;
      let plantNote = "";
      if (usersSnap.truncated) {
        const activeIds = Array.from(new Set(actions.map((a) => a.userId).filter(Boolean)));
        const activeSnap = await fetchUsersByIds(activeIds.slice(0, 100));
        if (!activeSnap.error && activeSnap.data.length > 0) {
          plantUsers = activeSnap.data;
          plantNote = ACTIVE_USER_SAMPLE_LABEL;
        } else {
          plantNote = "Sampled (limit hit)";
        }
      }
      const plantDist = computePlantDistribution(plantUsers, usersSnap.truncated, plantNote);
      const kpis = computeKpis(
        waterings.data,
        chats.data,
        plantUsers,
        range.end,
        monthRange,
        actions
      );

      const habitMetrics = computeHabitMetrics(actions);
      const emotionalMetrics = computeEmotionalMetrics(actions);

      setOverview(kpis);
      setPlantDistribution(plantDist);
      setDailySeries(daily);
      setStackedSeries(stacked);
      setHabit(habitMetrics);
      setEmotional(emotionalMetrics);

      cacheRef.current[rangeKey] = {
        kpis,
        plantDistribution: plantDist,
        dailySeries: daily,
        stackedSeries: stacked,
        habit: habitMetrics,
        emotional: emotionalMetrics,
      };
    } catch (err) {
      console.error("Overview load failed", err);
      setError("Failed to load overview data.");
    } finally {
      setLoading((s) => ({ ...s, overview: false }));
    }
  };

  const loadRetention = async () => {
    setLoading((s) => ({ ...s, retention: true }));
    setError("");
    try {
      const windows = buildCohortWindows(retentionMode, cohortCount);
      if (windows.length === 0) {
        setRetention({ rows: [], trend: [] });
        setLoading((s) => ({ ...s, retention: false }));
        return;
      }
      const start = windows[0].start;
      const end = windows[windows.length - 1].end;
      const userSnap = await fetchUsersInRange(start, end, cohortCount);
      if (userSnap.error) {
        setError("Missing permission to read Users for retention.");
        setLoading((s) => ({ ...s, retention: false }));
        return;
      }
      const actionsRangeEnd = new Date(end);
      actionsRangeEnd.setDate(actionsRangeEnd.getDate() + 120);
      const { waterings, chats } = await fetchCareActions(start, actionsRangeEnd);
      if (waterings.error || chats.error) {
        setError("Missing permission to read care actions for retention.");
        setLoading((s) => ({ ...s, retention: false }));
        return;
      }
      const actions = mergeCareActions(waterings.data, chats.data);
      const data = computeRetention(userSnap.data, actions, windows);
      setRetention(data);
    } catch (err) {
      console.error("Retention load failed", err);
      setError("Failed to load retention data.");
    } finally {
      setLoading((s) => ({ ...s, retention: false }));
    }
  };

  const loadMonthlyReport = async () => {
    setLoading((s) => ({ ...s, monthly: true }));
    setError("");
    try {
      const ranges = getLastFullMonthRanges();
      const { prevMonthStart, lastMonthStart, lastMonthEnd, prevMonthEnd } = ranges;

      const { waterings, chats } = await fetchCareActions(prevMonthStart, lastMonthEnd);
      if (waterings.error || chats.error) {
        setError("Missing permission to read care actions for monthly report.");
        setLoading((s) => ({ ...s, monthly: false }));
        return;
      }
      const actions = mergeCareActions(waterings.data, chats.data);
      const lastMonthActions = actions.filter(
        (a) => a.createdAt >= lastMonthStart && a.createdAt < lastMonthEnd
      );
      const prevMonthActions = actions.filter(
        (a) => a.createdAt >= prevMonthStart && a.createdAt < lastMonthStart
      );

      const usersSnap = await fetchUsersBefore(lastMonthEnd);
      if (usersSnap.error) {
        setError("Missing permission to read Users for monthly report.");
        setLoading((s) => ({ ...s, monthly: false }));
        return;
      }
      const lastUsers = usersSnap.data.filter((u) => u.createdAt < lastMonthEnd);
      const prevUsers = usersSnap.data.filter((u) => u.createdAt < prevMonthEnd);

      const report = computeMonthlyReport(lastMonthActions, prevMonthActions, lastUsers, prevUsers, {
        lastMonthStart,
        lastMonthEnd,
        prevMonthStart,
        prevMonthEnd,
      });
      setMonthlyReport(report);
    } catch (err) {
      console.error("Monthly report load failed", err);
      setError("Failed to load monthly report.");
    } finally {
      setLoading((s) => ({ ...s, monthly: false }));
    }
  };

  const onPresetChange = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const nextRange = getPresetRange(key);
    setRange(nextRange);
    setCustomRange({ start: "", end: "" });
  };

  const onCustomChange = (field, value) => {
    const next = { ...customRange, [field]: value };
    setCustomRange(next);
    if (next.start && next.end) {
      const clamped = clampRange({ start: next.start, end: next.end });
      if (clamped) {
        setPreset("custom");
        setRange(clamped);
      }
    }
  };

  const downloadMonthlyJson = () => {
    if (!monthlyReport) return;
    const blob = new Blob([JSON.stringify(monthlyReport, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "analytics-monthly-report.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpiCards = overview ? (
    <div className="kpiGrid">
      <KPICard label="Active users" value={overview.activeUsers} />
      <KPICard label="Total waterings" value={overview.totalWaterings} />
      <KPICard label="Total chats" value={overview.totalChats} />
      <KPICard label="Total care actions" value={overview.totalCareActions} />
      <KPICard
        label="Care actions / active user"
        value={overview.activeUsers ? overview.careActionsPerActiveUser.toFixed(2) : "0"}
      />
      <KPICard
        label="Chat to watering ratio"
        value={
          overview.totalWaterings === 0
            ? overview.totalChats
            : overview.chatToWateringRatio.toFixed(2)
        }
      />
      <KPICard
        label="% users with 2+ plants"
        value={`${overview.pctUsersWith2PlusPlants.toFixed(1)}%`}
      />
      <KPICard
        label="Warm users (month)"
        value={overview.warmUsers != null ? overview.warmUsers : "—"}
        helper="Care actions in 2+ PT weeks"
      />
    </div>
  ) : null;

  const dailyTotalBars = dailySeries.map((d) => ({ label: d.date, value: d.total }));
  const retentionTrend = retention?.trend?.map((t) => ({ label: t.label, value: t.value })) || [];

  if (authError) {
    return (
      <div className="analyticsContainer">
        <ConsoleHomePageHeader />
        <ErrorBanner message={authError} />
      </div>
    );
  }

  return (
    <div className="analyticsContainer">
      <ConsoleHomePageHeader />
      <div className="filterBar">
        <div className="filterGroup">
          <label>Date range</label>
          <select value={preset} onChange={(e) => onPresetChange(e.target.value)}>
            {DATE_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        {preset === "custom" ? (
          <div className="filterGroup">
            <label>Start</label>
            <input
              type="date"
              value={customRange.start}
              onChange={(e) => onCustomChange("start", e.target.value)}
            />
          </div>
        ) : null}
        {preset === "custom" ? (
          <div className="filterGroup">
            <label>End</label>
            <input
              type="date"
              value={customRange.end}
              onChange={(e) => onCustomChange("end", e.target.value)}
            />
          </div>
        ) : null}
        <div className="filterGroup">
          <label>Current</label>
          <div className="rangeLabel">
            {formatDateKeyPT(range.start)} → {formatDateKeyPT(range.end)}
          </div>
        </div>
      </div>

      <ErrorBanner message={error} />

      <section className="section">
        <div className="sectionHeader">
          <h2>Overview</h2>
        </div>
        {loading.overview ? <LoadingText /> : kpiCards}
        <div className="chartGrid">
          <div className="chartBox">
            <div className="chartTitle">Daily care actions</div>
            {loading.overview ? <LoadingText /> : <TinyBarChart data={dailyTotalBars} primary="#7c3aed" />}
          </div>
          <div className="chartBox">
            <div className="chartTitle">Waterings vs chats (stacked)</div>
            {loading.overview ? (
              <LoadingText />
            ) : (
              <StackedBars
                data={stackedSeries}
                colors={{ waterings: "#34a853", chats: "#1a73e8" }}
              />
            )}
          </div>
          <div className="chartBox">
            <div className="chartTitle">Plant count distribution</div>
            {loading.overview ? (
              <LoadingText />
            ) : (
              <DistributionBar
                buckets={plantDistribution?.buckets || []}
                note={plantDistribution?.note}
              />
            )}
          </div>
        </div>
        <div className="chartGrid two">
          <div className="chartBox">
            <div className="chartTitle">Habit gaps (days between actions)</div>
            {loading.overview ? (
              <LoadingText />
            ) : habit && habit.samples > 0 ? (
              <div>
                <div className="statRow">
                  <span>Median</span>
                  <strong>{habit.median.toFixed(1)}d</strong>
                </div>
                <div className="statRow">
                  <span>P25 / P75</span>
                  <strong>
                    {habit.p25.toFixed(1)}d / {habit.p75.toFixed(1)}d
                  </strong>
                </div>
                <div className="histogram">
                  {habit.histogram.map((b) => (
                    <div key={b.label} className="histogramBar">
                      <div style={{ height: `${Math.min(100, b.count * 6)}px` }} />
                      <span>
                        {b.label}: {b.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="chartEmpty">Not enough action history</div>
            )}
          </div>
          <div className="chartBox">
            <div className="chartTitle">Emotional engagement (chat-only days)</div>
            {loading.overview ? (
              <LoadingText />
            ) : (
              <div className="statRow">
                <span>Chat-only days %</span>
                <strong>{emotional ? emotional.chatOnlyDaysPct.toFixed(1) : "0"}%</strong>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="sectionHeader">
          <h2>Retention</h2>
          <div className="retentionControls">
            <button
              className={retentionMode === "weekly" ? "pill active" : "pill"}
              onClick={() => setRetentionMode("weekly")}
            >
              Weekly
            </button>
            <button
              className={retentionMode === "monthly" ? "pill active" : "pill"}
              onClick={() => setRetentionMode("monthly")}
            >
              Monthly
            </button>
            <button className="pill" onClick={() => setCohortCount((c) => c + 6)}>
              Load more
            </button>
          </div>
        </div>
        {loading.retention ? <LoadingText /> : <RetentionTable rows={retention.rows} />}
        <div className="chartBox trendBox">
          <div className="chartTitle">D30 trend (recent cohorts)</div>
          {loading.retention ? (
            <LoadingText />
          ) : (
            <TinyBarChart data={retentionTrend} primary="#16a34a" />
          )}
        </div>
      </section>

      <section className="section">
        <div className="sectionHeader">
          <h2>Monthly report (last full month)</h2>
          <button className="pill" onClick={downloadMonthlyJson}>
            Download JSON
          </button>
        </div>
        {loading.monthly ? (
          <LoadingText />
        ) : monthlyReport ? (
          <div>
            <div className="monthGrid">
              <div className="monthCard">
                <div className="chartTitle">Last month</div>
                <div className="statRow">
                  <span>Care actions</span>
                  <strong>{monthlyReport.lastMonth.kpis.totalCareActions}</strong>
                </div>
                <div className="statRow">
                  <span>Active users</span>
                  <strong>{monthlyReport.lastMonth.kpis.activeUsers}</strong>
                </div>
                <div className="statRow">
                  <span>Median gap</span>
                  <strong>
                    {monthlyReport.lastMonth.habit.median
                      ? `${monthlyReport.lastMonth.habit.median.toFixed(1)}d`
                      : "—"}
                  </strong>
                </div>
              </div>
              <div className="monthCard">
                <div className="chartTitle">Previous month</div>
                <div className="statRow">
                  <span>Care actions</span>
                  <strong>{monthlyReport.prevMonth.kpis.totalCareActions}</strong>
                </div>
                <div className="statRow">
                  <span>Active users</span>
                  <strong>{monthlyReport.prevMonth.kpis.activeUsers}</strong>
                </div>
                <div className="statRow">
                  <span>Median gap</span>
                  <strong>
                    {monthlyReport.prevMonth.habit.median
                      ? `${monthlyReport.prevMonth.habit.median.toFixed(1)}d`
                      : "—"}
                  </strong>
                </div>
              </div>
            </div>
            <div className="insights">
              <div className="chartTitle">Insights</div>
              {monthlyReport.insights.length === 0 ? (
                <div className="chartEmpty">No automated insights for this month</div>
              ) : (
                monthlyReport.insights.map((item, idx) => (
                  <div key={idx} className="insightItem">
                    {item}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="chartEmpty">No monthly data</div>
        )}
      </section>
    </div>
  );
};

export default ConsoleAnalyticsV2;
